import { useEffect, useRef, useState } from 'react';
import Donate from './Donate';

const items = [
  {
    image:       '/images/v1/img13.jpg',
    badge:       'Community',
    title:       'Become a Field Observer',
    description: 'Join our network of trained community observers who monitor wildlife activity, document incidents, and submit verified reports that feed directly into the WITS intelligence layer.',
    type:        'email' as const,
    cta:         { label: 'Apply Now', href: 'mailto:info@lapa.africa?subject=Becoming a WITS Volunteer' },
  },
  {
    image:       '/images/v1/img14.jpg',
    badge:       'Support',
    title:       'Fund the Mission',
    description: 'Every contribution directly funds platform development, field equipment for rangers, and legal support for the prosecutors building wildlife crime cases across Africa.',
    type:        'donate' as const,
    cta:         { label: 'Donate', href: null },
  },
  {
    image:       '/images/v1/img15.jpg',
    badge:       'Partnership',
    title:       'Institutional Partnership',
    description: "NGOs, governments, research institutions, and conservation bodies — collaborate with us to expand WITS's reach, share data, and co-develop policies that protect Africa's biodiversity.",
    type:        'email' as const,
    cta:         { label: 'Get in Touch', href: 'mailto:info@lapa.africa?subject=WITS Partnership Inquiry' },
  },
];

export default function GetInvolved() {
  const ref     = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative py-24 lg:py-32 overflow-hidden bg-[#052e16]">

      {/* Background image wash */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="/images/v1/img12.jpg"
          alt=""
          className="w-full h-full object-cover opacity-10 select-none"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#052e16] via-[#052e16]/80 to-[#052e16]" />
      </div>

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">

        {/* Header */}
        <div className={`text-center mb-16 reveal ${visible ? 'visible' : ''}`}>
          <span className="inline-flex items-center gap-3 mb-5">
            <span className="h-px w-8 bg-green-400" />
            <span
              className="text-green-400 text-xs tracking-[0.22em] uppercase font-medium"
              style={{ fontFamily: 'var(--font-body)' }}
            >
              Take Action
            </span>
            <span className="h-px w-8 bg-green-400" />
          </span>
          <h2
            className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-4"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Be Part of the{' '}
            <span className="text-green-400">Solution</span>
          </h2>
          <p
            className="text-white/50 max-w-xl mx-auto text-base leading-relaxed"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            Conservation is not a spectator sport. Here are the three most impactful ways to get involved.
          </p>
        </div>

        {/* Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((item, i) => (
            <div
              key={item.title}
              className={`reveal reveal-delay-${i + 1} ${visible ? 'visible' : ''} group flex flex-col rounded-2xl overflow-hidden border border-white/10 bg-white/5 hover:bg-white/8 hover:border-green-400/30 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#052e16]`}
            >
              {/* Image */}
              <div className="aspect-video overflow-hidden shrink-0">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* Body */}
              <div className="flex flex-col flex-1 p-7">
                <span
                  className="inline-block text-[10px] tracking-[0.18em] uppercase text-green-400/80 font-semibold mb-3"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {item.badge}
                </span>
                <h3
                  className="text-xl font-semibold text-white mb-3 leading-snug"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-white/50 text-sm leading-relaxed flex-1 mb-7"
                  style={{ fontFamily: 'var(--font-body)' }}
                >
                  {item.description}
                </p>

                {item.type === 'donate' ? (
                  <Donate />
                ) : (
                  <a
                    href={item.cta.href ?? '#'}
                    className="self-start inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-green-600 text-white text-sm font-semibold hover:bg-green-500 transition-all duration-300 hover:shadow-lg hover:shadow-green-900/40"
                    style={{ fontFamily: 'var(--font-body)' }}
                  >
                    {item.cta.label} →
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
